import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ImagePlus, Music, Film, Plus, Trash2, Wand2, Loader2,
  Play, Pause, Lock, GripVertical, Clock, Sparkles, Download, Zap,
  Share2, Copy, Check, Activity,
} from "lucide-react";
import { api, formatApiError, trackEvent } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ASPECTS = [
  { id: "16:9", label: "16:9", hint: "YouTube", box: "w-16 h-9" },
  { id: "9:16", label: "9:16", hint: "Reels / TikTok", box: "w-9 h-16" },
  { id: "1:1", label: "1:1", hint: "Carré", box: "w-12 h-12" },
];

let SID = 0;
const sid = () => `s${++SID}`;

const fmtTime = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export default function MvCreator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSub = !!user && (user.role === "admin" || ["Standard", "High / Studio", "Master Pro"].includes(user.plan));

  const [photoFile, setPhotoFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [audioDur, setAudioDur] = useState(0);
  const [aspect, setAspect] = useState("16:9");
  const [scenes, setScenes] = useState([
    { id: sid(), prompt: "", duration: "5", status: "idle", jobId: null, url: null, error: null, lipsync: false },
  ]);
  const [playing, setPlaying] = useState(false);
  const [preview, setPreview] = useState(null); // scene id being previewed
  const [theme, setTheme] = useState("");
  const [autoBusy, setAutoBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [exportUrl, setExportUrl] = useState(null);
  const [beatSync, setBeatSync] = useState(true);
  const [copied, setCopied] = useState(false);

  const audioRef = useRef(null);
  const pollers = useRef({}); // jobId -> intervalId
  const exportPoller = useRef(null);
  const dragIdx = useRef(null);

  useEffect(() => {
    trackEvent("pageview", "mv-creator", "/studio");
    return () => {
      Object.values(pollers.current).forEach(clearInterval);
      if (exportPoller.current) clearInterval(exportPoller.current);
    };
  }, []);

  // ---- uploads ----
  const onPhoto = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Merci de choisir une image (JPG/PNG)."); return; }
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoFile(file);
    setPhotoUrl(URL.createObjectURL(file));
  };

  const onAudio = (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) { toast.error("Merci de choisir un fichier audio (MP3/WAV)."); return; }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioFile(file);
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    const a = new Audio(url);
    a.addEventListener("loadedmetadata", () => {
      const d = a.duration;
      if (d === Infinity || isNaN(d)) {
        // Chrome/Safari MP3 workaround: seek to the end to force duration computation
        const onSeek = () => {
          a.removeEventListener("timeupdate", onSeek);
          const real = a.duration;
          setAudioDur(isFinite(real) ? real : 0);
          a.currentTime = 0;
        };
        a.addEventListener("timeupdate", onSeek);
        a.currentTime = 1e101;
      } else {
        setAudioDur(d || 0);
      }
    });
  };

  // ---- scenes ----
  const addScene = () =>
    setScenes((s) => [...s, { id: sid(), prompt: "", duration: "5", status: "idle", jobId: null, url: null, error: null, lipsync: false }]);

  const updateScene = (id, patch) =>
    setScenes((s) => s.map((sc) => (sc.id === id ? { ...sc, ...patch } : sc)));

  const removeScene = (id) => {
    setScenes((s) => (s.length > 1 ? s.filter((sc) => sc.id !== id) : s));
    if (preview === id) setPreview(null);
  };

  const totalVideoDur = scenes.reduce((n, s) => n + Number(s.duration || 0), 0);

  // ---- drag reorder ----
  const onDragStart = (i) => { dragIdx.current = i; };
  const onDrop = (i) => {
    const from = dragIdx.current;
    if (from == null || from === i) return;
    setScenes((s) => {
      const next = [...s];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIdx.current = null;
  };

  // ---- generation ----
  const pollJob = (id, jobId) => {
    if (pollers.current[jobId]) return;
    pollers.current[jobId] = setInterval(async () => {
      try {
        const { data } = await api.get(`/video/clip/${jobId}`);
        if (data.status === "done") {
          clearInterval(pollers.current[jobId]); delete pollers.current[jobId];
          updateScene(id, { status: "done", url: data.url });
          toast.success("Scène générée !");
        } else if (data.status === "error") {
          clearInterval(pollers.current[jobId]); delete pollers.current[jobId];
          updateScene(id, { status: "error", error: data.error || "Échec de la génération" });
          toast.error(data.error || "Échec de la génération");
        }
      } catch (e) { /* keep polling */ }
    }, 5000);
  };

  const generateScene = async (sc) => {
    if (!user) { toast.error("Connectez-vous pour générer des scènes."); navigate("/login"); return; }
    if (!isSub) { toast.error("La génération vidéo IA est réservée aux abonnés."); goPricing(); return; }
    if (!photoFile) { toast.error("Ajoutez d'abord une photo du personnage."); return; }
    updateScene(sc.id, { status: "queued", error: null, url: null });
    try {
      const fd = new FormData();
      fd.append("photo", photoFile);
      fd.append("prompt", sc.prompt || "");
      fd.append("aspect", aspect);
      fd.append("duration", sc.duration);
      if (sc.lipsync && audioFile) { fd.append("lipsync", "true"); fd.append("audio", audioFile); }
      const { data } = await api.post("/video/clip", fd);
      updateScene(sc.id, { status: "processing", jobId: data.job_id });
      trackEvent("video_generate", aspect, "/studio");
      pollJob(sc.id, data.job_id);
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail);
      updateScene(sc.id, { status: "error", error: msg });
      toast.error(msg);
    }
  };

  const runAutoPlan = async () => {
    if (!user) { toast.error("Connectez-vous d'abord."); navigate("/login"); return; }
    if (!isSub) { toast.error("Le mode auto est réservé aux abonnés."); goPricing(); return; }
    if (!audioFile) { toast.error("Ajoutez d'abord la musique."); return; }
    setAutoBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", audioFile);
      fd.append("theme", theme || "");
      const { data } = await api.post("/video/auto-plan", fd);
      setScenes((data.scenes || []).map((s) => ({
        id: sid(), prompt: s.prompt, duration: String(s.duration), status: "idle",
        jobId: null, url: null, error: null, lipsync: false,
      })));
      toast.success(`${data.scenes?.length || 0} scènes proposées par l'IA (${data.duration}s)`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setAutoBusy(false); }
  };

  const pollExport = (eid) => {
    if (exportPoller.current) clearInterval(exportPoller.current);
    exportPoller.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/video/export/${eid}`);
        setExportStatus(data.status);
        if (data.status === "done") {
          clearInterval(exportPoller.current); setExportUrl(data.url); setExportBusy(false);
          toast.success("Clip final prêt !");
        } else if (data.status === "error") {
          clearInterval(exportPoller.current); setExportBusy(false);
          toast.error(data.error || "Échec de l'export");
        }
      } catch (e) { /* keep polling */ }
    }, 5000);
  };

  const runExport = async () => {
    if (!user) { toast.error("Connectez-vous d'abord."); navigate("/login"); return; }
    if (!isSub) { toast.error("L'export est réservé aux abonnés."); goPricing(); return; }
    if (!audioFile) { toast.error("Ajoutez la musique pour l'export."); return; }
    const done = scenes.filter((s) => s.status === "done" && s.jobId);
    if (done.length === 0) { toast.error("Générez au moins une scène avant d'exporter."); return; }
    setExportBusy(true); setExportUrl(null); setExportStatus("queued");
    try {
      const fd = new FormData();
      fd.append("audio", audioFile);
      fd.append("job_ids", done.map((s) => s.jobId).join(","));
      fd.append("beat_sync", beatSync ? "true" : "false");
      const { data } = await api.post("/video/export", fd);
      trackEvent("video_export", null, "/studio");
      pollExport(data.export_id);
    } catch (e) {
      setExportBusy(false);
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const generateAll = () => {
    scenes.filter((s) => s.status === "idle" || s.status === "error").forEach(generateScene);
  };

  const goPricing = () => { navigate("/"); setTimeout(() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }), 400); };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  const videoBox = aspect === "9:16" ? "aspect-[9/16] max-w-[220px]" : aspect === "1:1" ? "aspect-square max-w-[320px]" : "aspect-video";
  const doneCount = scenes.filter((s) => s.status === "done").length;

  const shareId = exportUrl ? exportUrl.split("/").pop() : null;
  const shareUrl = shareId ? `${window.location.origin}/clip/${shareId}` : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast.success("Lien copié !"); setTimeout(() => setCopied(false), 2000); }
    catch (e) { toast.error("Copie impossible"); }
  };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Mon clip TUBMIX", url: shareUrl }); } catch (e) { /* cancelled */ } }
    else { copyLink(); }
  };

  return (
    <div translate="no" className="min-h-screen bg-[#0B0D12] text-[#F8FAFC] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0B0D12]/95 backdrop-blur border-b border-[#262C3A] px-3 sm:px-5 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate("/")} className="text-[#64748B] hover:text-white transition-colors" data-testid="creator-back-button">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Logo className="h-7 w-auto" />
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-xs font-mono uppercase bg-[#FF5500]/15 text-[#FF5500] px-2 py-0.5 rounded-full">Créateur de clip IA</span>
            <span className="text-[10px] font-mono uppercase bg-[#8B5CF6]/15 text-[#8B5CF6] px-2 py-0.5 rounded-full">fal.ai · Kling</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {!user && (
              <button onClick={() => navigate("/login")} data-testid="creator-login-cta"
                className="bg-[#FF5500] hover:bg-[#e64d00] text-white rounded-xl px-4 py-2 text-sm font-semibold transition-colors">
                Se connecter
              </button>
            )}
            {user && !isSub && (
              <button onClick={goPricing} data-testid="creator-upgrade-cta"
                className="bg-[#FF5500] hover:bg-[#e64d00] text-white rounded-xl px-4 py-2 text-sm font-semibold transition-colors">
                Voir les tarifs
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-3 sm:px-5 py-6">
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Transformez une photo en clip musical</h1>
          <p className="text-[#94A3B8] mt-2 text-sm sm:text-base">
            Ajoutez une photo de votre personnage et votre musique, décrivez vos scènes, puis générez un clip vidéo IA calé sur la musique.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Left column: inputs */}
          <div className="space-y-5">
            {/* Photo */}
            <section className="bg-[#131722] border border-[#262C3A] rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-[#FF5500] text-white text-xs font-bold">1</span>
                <h2 className="font-bold">Photo du personnage</h2>
              </div>
              <label data-testid="photo-dropzone"
                className="group relative flex flex-col items-center justify-center border-2 border-dashed border-[#262C3A] hover:border-[#FF5500] rounded-xl cursor-pointer transition-colors overflow-hidden"
                style={{ minHeight: 160 }}>
                {photoUrl ? (
                  <img src={photoUrl} alt="aperçu" className="w-full h-40 object-cover" data-testid="photo-preview" />
                ) : (
                  <div className="flex flex-col items-center text-[#64748B] group-hover:text-[#FF5500] py-8">
                    <ImagePlus className="h-8 w-8 mb-2" />
                    <span className="text-sm">Cliquez pour choisir une image</span>
                    <span className="text-xs text-[#475569]">JPG / PNG</span>
                  </div>
                )}
                <input type="file" accept="image/*" className="hidden" data-testid="photo-input"
                  onChange={(e) => onPhoto(e.target.files?.[0])} />
              </label>
              {photoUrl && (
                <button onClick={() => { if (photoUrl) URL.revokeObjectURL(photoUrl); setPhotoFile(null); setPhotoUrl(""); }}
                  className="mt-2 text-xs text-[#94A3B8] hover:text-white">Changer la photo</button>
              )}
            </section>

            {/* Audio */}
            <section className="bg-[#131722] border border-[#262C3A] rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-[#FF5500] text-white text-xs font-bold">2</span>
                <h2 className="font-bold">Musique</h2>
                <span className="ml-auto text-[10px] text-[#64748B]">non modifiée</span>
              </div>
              {!audioUrl ? (
                <label data-testid="audio-dropzone"
                  className="group flex flex-col items-center justify-center border-2 border-dashed border-[#262C3A] hover:border-[#FF5500] rounded-xl cursor-pointer transition-colors py-8 text-[#64748B] hover:text-[#FF5500]">
                  <Music className="h-8 w-8 mb-2" />
                  <span className="text-sm">Choisir un fichier audio</span>
                  <span className="text-xs text-[#475569]">MP3 / WAV</span>
                  <input type="file" accept="audio/*" className="hidden" data-testid="audio-input"
                    onChange={(e) => onAudio(e.target.files?.[0])} />
                </label>
              ) : (
                <div className="bg-[#0B0D12] border border-[#262C3A] rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <button onClick={togglePlay} data-testid="audio-play-button"
                      className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF5500] hover:bg-[#e64d00] text-white transition-colors">
                      {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{audioFile?.name}</div>
                      <div className="text-xs text-[#64748B]">Durée : {fmtTime(audioDur)}</div>
                    </div>
                  </div>
                  <audio ref={audioRef} src={audioUrl} onEnded={() => setPlaying(false)} className="hidden" data-testid="audio-player" />
                  <label className="mt-2 inline-block text-xs text-[#94A3B8] hover:text-white cursor-pointer">
                    Changer la musique
                    <input type="file" accept="audio/*" className="hidden" onChange={(e) => onAudio(e.target.files?.[0])} />
                  </label>
                </div>
              )}
            </section>

            {/* Aspect */}
            <section className="bg-[#131722] border border-[#262C3A] rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-[#FF5500] text-white text-xs font-bold">3</span>
                <h2 className="font-bold">Format</h2>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {ASPECTS.map((a) => (
                  <button key={a.id} onClick={() => setAspect(a.id)} data-testid={`aspect-${a.id}`}
                    className={`flex flex-col items-center gap-2 rounded-xl border p-3 transition-colors ${
                      aspect === a.id ? "border-[#FF5500] bg-[#FF5500]/10" : "border-[#262C3A] hover:border-[#475569]"}`}>
                    <div className="flex items-center justify-center h-16">
                      <div className={`${a.box} rounded border-2 ${aspect === a.id ? "border-[#FF5500]" : "border-[#475569]"}`} />
                    </div>
                    <span className="text-xs font-bold">{a.label}</span>
                    <span className="text-[10px] text-[#64748B]">{a.hint}</span>
                  </button>
                ))}
              </div>
            </section>
          </div>

          {/* Right column: scenes */}
          <div className="lg:col-span-2 space-y-5">
            {/* Mode Auto */}
            <section className="bg-gradient-to-br from-[#1a1030] to-[#101826] border border-[#8B5CF6]/40 rounded-2xl p-4" data-testid="auto-mode">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4 text-[#8B5CF6]" />
                <h2 className="font-bold">Mode Auto IA</h2>
                <span className="text-[10px] font-mono uppercase bg-[#8B5CF6]/20 text-[#8B5CF6] px-2 py-0.5 rounded-full">librosa · Claude</span>
              </div>
              <p className="text-xs text-[#94A3B8] mb-3">L'IA écoute votre musique et propose automatiquement les scènes (nombre, durées) et des descriptions créatives.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="auto-theme-input"
                  placeholder="Thème / ambiance (ex: clip afro love, plage au coucher de soleil)"
                  className="flex-1 bg-[#0B0D12] border border-[#262C3A] focus:border-[#8B5CF6] rounded-lg px-3 py-2 text-sm outline-none" />
                <button onClick={runAutoPlan} disabled={autoBusy || !audioFile} data-testid="auto-plan-button"
                  className="inline-flex items-center justify-center gap-2 bg-[#8B5CF6] hover:bg-[#7c4ddb] disabled:opacity-40 text-white rounded-lg px-4 py-2 text-sm font-bold transition-colors whitespace-nowrap">
                  {autoBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyse…</> : <><Sparkles className="h-4 w-4" /> Proposer les scènes</>}
                </button>
              </div>
              {!audioFile && <div className="text-[11px] text-[#64748B] mt-2">Ajoutez d'abord la musique (étape 2) pour activer le mode auto.</div>}
            </section>

            <section className="bg-[#131722] border border-[#262C3A] rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-[#FF5500] text-white text-xs font-bold">4</span>
                <h2 className="font-bold">Scènes</h2>
                <span className="text-xs text-[#64748B]">({scenes.length})</span>
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={addScene} data-testid="add-scene-button"
                    className="inline-flex items-center gap-1.5 bg-[#1A1F2C] hover:bg-[#262C3A] rounded-xl px-3 py-2 text-sm font-semibold transition-colors">
                    <Plus className="h-4 w-4" /> Scène
                  </button>
                  <button onClick={generateAll} data-testid="generate-all-button"
                    className="inline-flex items-center gap-1.5 bg-[#FF5500] hover:bg-[#e64d00] text-white rounded-xl px-3 py-2 text-sm font-bold transition-colors">
                    <Wand2 className="h-4 w-4" /> Tout générer
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {scenes.map((sc, i) => (
                  <div key={sc.id} data-testid={`scene-card-${i}`}
                    className="bg-[#0B0D12] border border-[#262C3A] rounded-xl p-3">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col items-center pt-1 text-[#475569]">
                        <span className="text-xs font-bold text-[#64748B]">#{i + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <textarea rows={2} value={sc.prompt} data-testid={`scene-prompt-${i}`}
                          onChange={(e) => updateScene(sc.id, { prompt: e.target.value })}
                          placeholder="Décrivez la scène (ex: plan rapproché, lumière néon, la personne chante, caméra lente)"
                          className="w-full bg-[#131722] border border-[#262C3A] focus:border-[#FF5500] rounded-lg px-3 py-2 text-sm outline-none resize-none" />
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <div className="inline-flex items-center gap-1 text-xs text-[#94A3B8]">
                            <Clock className="h-3.5 w-3.5" />
                            {["5", "10"].map((d) => (
                              <button key={d} onClick={() => updateScene(sc.id, { duration: d })} data-testid={`scene-duration-${i}-${d}`}
                                className={`rounded-md px-2 py-1 font-semibold transition-colors ${sc.duration === d ? "bg-[#FF5500] text-white" : "bg-[#1A1F2C] hover:bg-[#262C3A]"}`}>
                                {d}s
                              </button>
                            ))}
                          </div>
                          <label className={`inline-flex items-center gap-1.5 text-xs ${audioFile ? "text-[#94A3B8] cursor-pointer" : "text-[#475569] cursor-not-allowed"}`}
                            title={audioFile ? "Lip-sync sur la musique" : "Ajoutez la musique pour activer le lip-sync"}>
                            <input type="checkbox" disabled={!audioFile} checked={sc.lipsync} data-testid={`scene-lipsync-${i}`}
                              onChange={(e) => updateScene(sc.id, { lipsync: e.target.checked })} className="accent-[#FF5500]" />
                            Lip-sync {!audioFile && <span className="text-[9px] bg-[#1A1F2C] px-1 rounded">musique requise</span>}
                          </label>
                          <div className="ml-auto flex items-center gap-2">
                            <button onClick={() => generateScene(sc)} disabled={sc.status === "queued" || sc.status === "processing"}
                              data-testid={`generate-scene-${i}`}
                              className="inline-flex items-center gap-1.5 bg-[#8B5CF6] hover:bg-[#7c4ddb] disabled:opacity-40 text-white rounded-lg px-3 py-1.5 text-xs font-bold transition-colors">
                              {sc.status === "queued" || sc.status === "processing"
                                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> En cours…</>
                                : <><Sparkles className="h-3.5 w-3.5" /> Générer</>}
                            </button>
                            <button onClick={() => removeScene(sc.id)} data-testid={`remove-scene-${i}`}
                              className="text-[#64748B] hover:text-[#EF4444] transition-colors" title="Supprimer">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        {sc.status === "done" && sc.url && (
                          <video src={`${BACKEND}${sc.url}`} controls data-testid={`scene-video-${i}`}
                            className="mt-3 w-full max-w-[360px] rounded-lg border border-[#262C3A]" />
                        )}
                        {sc.status === "error" && (
                          <div className="mt-2 text-xs text-[#EF4444]" data-testid={`scene-error-${i}`}>{sc.error}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Timeline */}
            <section className="bg-[#131722] border border-[#262C3A] rounded-2xl p-4" data-testid="timeline">
              <div className="flex items-center gap-2 mb-3">
                <Film className="h-4 w-4 text-[#06B6D4]" />
                <h2 className="font-bold">Storyboard / Timeline</h2>
                <span className="ml-auto text-xs text-[#64748B]">
                  Vidéo : {totalVideoDur}s / Musique : {fmtTime(audioDur)}
                </span>
              </div>
              {audioDur > 0 && (
                <div className="mb-3">
                  <div className="h-1.5 w-full bg-[#0B0D12] rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF5500] transition-all"
                      style={{ width: `${Math.min(100, (totalVideoDur / audioDur) * 100)}%` }} />
                  </div>
                  <div className="text-[10px] text-[#64748B] mt-1">
                    {totalVideoDur < audioDur
                      ? `Il manque ${Math.round(audioDur - totalVideoDur)}s pour couvrir toute la musique`
                      : "La durée des scènes couvre la musique"}
                  </div>
                </div>
              )}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {scenes.map((sc, i) => (
                  <div key={sc.id} draggable onDragStart={() => onDragStart(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                    data-testid={`timeline-scene-${i}`}
                    className="group relative shrink-0 w-28 bg-[#0B0D12] border border-[#262C3A] hover:border-[#FF5500] rounded-lg p-2 cursor-grab active:cursor-grabbing transition-colors"
                    style={{ minHeight: 80 }}>
                    <div className="flex items-center justify-between text-[10px] text-[#64748B] mb-1">
                      <span className="font-bold">#{i + 1}</span>
                      <GripVertical className="h-3 w-3" />
                    </div>
                    {sc.status === "done" && sc.url ? (
                      <video src={`${BACKEND}${sc.url}`} muted className="w-full h-12 object-cover rounded" />
                    ) : (
                      <div className={`w-full h-12 rounded flex items-center justify-center text-[9px] ${
                        sc.status === "processing" || sc.status === "queued" ? "bg-[#8B5CF6]/15 text-[#8B5CF6]"
                        : sc.status === "error" ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[#1A1F2C] text-[#475569]"}`}>
                        {sc.status === "processing" || sc.status === "queued" ? <Loader2 className="h-4 w-4 animate-spin" />
                          : sc.status === "error" ? "erreur" : `${sc.duration}s`}
                      </div>
                    )}
                    <div className="truncate text-[9px] text-[#94A3B8] mt-1">{sc.prompt || "sans titre"}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-[#64748B]">
                Astuce : glissez-déposez les vignettes pour réordonner les scènes. {doneCount}/{scenes.length} générées.
              </div>
            </section>

            {/* Export final */}
            <section className="bg-gradient-to-br from-[#0e2033] to-[#101826] border border-[#1e5a7a] rounded-2xl p-4" data-testid="export-section">
              <div className="flex items-center gap-2 mb-2">
                <Download className="h-4 w-4 text-[#06B6D4]" />
                <h2 className="font-bold">Export du clip final</h2>
                <span className="text-[10px] font-mono uppercase bg-[#06B6D4]/20 text-[#06B6D4] px-2 py-0.5 rounded-full">FFmpeg · MP4</span>
              </div>
              <p className="text-xs text-[#94A3B8] mb-3">
                Assemble les scènes dans l'ordre de la timeline et superpose votre musique (non modifiée). La vidéo est calée sur la durée de la chanson.
              </p>
              <label className="inline-flex items-center gap-2 text-sm mb-3 cursor-pointer select-none" title="Coupe les scènes sur les temps forts de la musique">
                <input type="checkbox" checked={beatSync} onChange={(e) => setBeatSync(e.target.checked)}
                  data-testid="export-beatsync" className="accent-[#06B6D4] h-4 w-4" />
                <Activity className="h-4 w-4 text-[#06B6D4]" />
                Montage sur les temps forts <span className="text-[10px] text-[#64748B]">(coupes rythmées sur la musique)</span>
              </label>
              <br />
              <button onClick={runExport} disabled={exportBusy || doneCount === 0 || !audioFile} data-testid="export-button"
                className="inline-flex items-center justify-center gap-2 bg-[#06B6D4] hover:bg-[#0598b4] disabled:opacity-40 text-white rounded-xl px-5 py-3 font-bold transition-colors">
                {exportBusy
                  ? <><Loader2 className="h-5 w-5 animate-spin" /> Assemblage… ({exportStatus === "processing" ? "montage" : "en file"})</>
                  : <><Film className="h-5 w-5" /> Exporter le clip (MP4)</>}
              </button>
              {(!audioFile || doneCount === 0) && (
                <div className="text-[11px] text-[#64748B] mt-2">
                  {!audioFile ? "Ajoutez la musique (étape 2) et générez au moins une scène." : "Générez au moins une scène pour exporter."}
                </div>
              )}
              {exportUrl && (
                <div className="mt-4 bg-[#0B0D12] border border-[#262C3A] rounded-xl p-3" data-testid="export-result">
                  <video src={`${BACKEND}${exportUrl}`} controls className="w-full max-w-[480px] rounded-lg border border-[#262C3A]" data-testid="export-video" />
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <a href={`${BACKEND}${exportUrl}`} download="clip-tubmix.mp4" data-testid="export-download"
                      className="inline-flex items-center gap-2 bg-[#10B981]/15 hover:bg-[#10B981]/25 text-[#10B981] rounded-lg px-4 py-2 text-sm font-semibold transition-colors">
                      <Download className="h-4 w-4" /> Télécharger
                    </a>
                    <button onClick={nativeShare} data-testid="share-button"
                      className="inline-flex items-center gap-2 bg-[#FF5500]/15 hover:bg-[#FF5500]/25 text-[#FF5500] rounded-lg px-4 py-2 text-sm font-semibold transition-colors">
                      <Share2 className="h-4 w-4" /> Partager
                    </button>
                    <button onClick={copyLink} data-testid="copy-link-button"
                      className="inline-flex items-center gap-2 bg-[#1A1F2C] hover:bg-[#262C3A] text-[#94A3B8] rounded-lg px-4 py-2 text-sm font-semibold transition-colors">
                      {copied ? <><Check className="h-4 w-4 text-[#10B981]" /> Copié</> : <><Copy className="h-4 w-4" /> Copier le lien</>}
                    </button>
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-xs text-[#64748B]">
                    <span>Partager sur :</span>
                    <a href={`https://wa.me/?text=${encodeURIComponent("Regarde mon clip TUBMIX : " + shareUrl)}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp" className="hover:text-[#25D366] transition-colors">WhatsApp</a>
                    <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Mon clip créé avec TUBMIX 🎬")}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" data-testid="share-x" className="hover:text-white transition-colors">X</a>
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" data-testid="share-facebook" className="hover:text-[#1877F2] transition-colors">Facebook</a>
                  </div>
                  <div className="mt-2 text-[11px] text-[#475569] break-all" data-testid="share-url">{shareUrl}</div>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
